import { RequestManager } from '@jaseeey/request-manager';
import { usePiniaManager } from 'composables/pinia-manager';
import { MaxAgeOption } from 'const/max-age-option';
import { EmploymentTypeOption } from 'const/employment-type-option';
import { RemunerationCycleOption } from 'const/remuneration-cycle-option';
import { SortOption } from 'const/sort-option';
import { WorkLocationOption } from 'const/work-location-option';
import { defineStore } from 'pinia';
import env from 'src/configs/env';
import { useJobTitleSearchStore } from 'stores/job-title-search-store';
import { useLocationStore } from 'stores/location-store';
import { computed, ref } from 'vue';

export const useJobSearchStore = defineStore('job-search', () => {

    /** @type {ReadonlyArray<EmploymentType>} */
    const employmentTypeOptions = Object.freeze([
        EmploymentTypeOption.FullTime,
        EmploymentTypeOption.PartTime,
        EmploymentTypeOption.Contract,
        EmploymentTypeOption.Casual,
        EmploymentTypeOption.Temporary,
        EmploymentTypeOption.Voluntary,
        EmploymentTypeOption.Other
    ]);

    /** @type {ReadonlyArray<MaxAgeFilter>} */
    const maxAgeOptions = Object.freeze([
        MaxAgeOption.LastDay,
        MaxAgeOption.Last3Days,
        MaxAgeOption.Last7Days,
        MaxAgeOption.Last14Days,
        MaxAgeOption.Last28Days,
        MaxAgeOption.AllTime
    ]);

    /** @type {ReadonlyArray<RemunerationCycleFilter>} */
    const remunerationCycleOptions = Object.freeze([
        RemunerationCycleOption.Year,
        RemunerationCycleOption.Day,
        RemunerationCycleOption.Hour
    ]);

    /** @type {ReadonlyArray<Sort>} */
    const sortOptions = Object.freeze([
        SortOption.Relevance,
        SortOption.Distance,
        SortOption.Recent
    ]);

    /** @type {ReadonlyArray<WorkLocation>} */
    const workLocationOptions = Object.freeze([
        WorkLocationOption.OnSite,
        WorkLocationOption.Hybrid,
        WorkLocationOption.Remote
    ]);

    const jobTitleSearchStore = useJobTitleSearchStore();
    const locationStore = useLocationStore();
    const piniaManager = usePiniaManager();

    const searchResults = ref([]);
    /** @type {SortOption|null} */
    const searchSort = ref(sortOptions[0].value);
    /** @type {import('vue').Ref<SearchFilters>} */
    const searchFilters = ref({
        title: null,
        location: null,
        classificationId: [],
        employmentType: [],
        workLocation: [],
        remunerationCycle: null,
        remunerationMin: null,
        remunerationMax: null,
        exclusionTerm: [],
        maxAge: null,
        expandRemote: true
    });

    const appliedSearchFiltersCount = computed(() => {
        let count = 0;
        if (searchFilters.value.classificationId && searchFilters.value.classificationId.length > 0) count++;
        if (searchFilters.value.employmentType && searchFilters.value.employmentType.length > 0) count++;
        if (searchFilters.value.workLocation && searchFilters.value.workLocation.length > 0) count++;
        if (searchFilters.value.remunerationMin || searchFilters.value.remunerationMax) count++;
        if (searchFilters.value.exclusionTerm && searchFilters.value.exclusionTerm.length > 0) count++;
        return count;
    });

    const loadedJobIds = ref(new Set());
    const loadedJobScopeMap = ref({});

    /** @type {import('vue').Ref<boolean>} */
    const searchLoadingState = ref(false);
    /** @type {import('vue').Ref<Date|null>} */
    const searchLoadedTimestamp = ref();

    /** @type {import('vue').Ref<SearchFilters>}*/
    const loadedSearchFilters = ref({});
    const loadedSearchSort = ref(null);
    const lastSearchId = ref(null);
    const lastPage = ref(0);
    const lastLimit = ref(20);
    const totalRecords = ref(0);
    const totalPages = ref(0);

    const isSearchLoading = computed(() => searchLoadingState.value);
    const isSearchLoaded = computed(() => Boolean(!!searchLoadedTimestamp.value));
    const hasMoreSearchResults = computed(() => lastPage.value < totalPages.value);

    const findJobByScopeId = computed(() => (jobScopeId) => {
        const jobId = loadedJobScopeMap.value[jobScopeId];
        if (!jobId) {
            return null;
        }
        return searchResults.value.find(j => j.id === jobId);
    });

    async function loadSearchResults(page = 1, limit = 20, overwrite = true) {
        try {
            searchLoadingState.value = true;
            let shouldSearchSimilarJobTitles;
            if (searchFilters.value.title && (!isSearchLoaded.value || searchFilters.value.title !== loadedSearchFilters.value.title)) {
                shouldSearchSimilarJobTitles = true;
                jobTitleSearchStore.prepareSimilarJobTitlesForSearch();
            } else if (!searchFilters.value.title) {
                shouldSearchSimilarJobTitles = false;
                jobTitleSearchStore.clearSimilarJobTitles();
            }
            const queryParams = prepareFilterParams(overwrite ? searchFilters.value : loadedSearchFilters.value);
            queryParams.append('page', page);
            queryParams.append('limit', limit);
            const queryString = queryParams.toString();
            const headers = { authorization: generateBasicAuthToken() };
            if (!overwrite && lastSearchId.value) {
                headers['X-Search-ID'] = lastSearchId.value;
            }

            await RequestManager.call(this.$api, 'get', `/public/jobs?${queryString}`, {}, { headers }, res => {
                if (overwrite) {
                    searchResults.value = [];
                    loadedJobIds.value.clear();
                    loadedJobScopeMap.value = {};
                    loadedSearchFilters.value = {
                        title: searchFilters.value.title,
                        location: searchFilters.value.location,
                        classificationId: searchFilters.value.classificationId,
                        employmentType: searchFilters.value.employmentType,
                        workLocation: searchFilters.value.workLocation,
                        remunerationCycle: searchFilters.value.remunerationCycle,
                        remunerationMin: searchFilters.value.remunerationMin,
                        remunerationMax: searchFilters.value.remunerationMax,
                        exclusionTerm: searchFilters.value.exclusionTerm,
                        maxAge: searchFilters.value.maxAge,
                        expandRemote: searchFilters.value.expandRemote
                    };
                    loadedSearchSort.value = searchSort.value;
                }
                res.data.forEach(syncCachedJob);
                searchLoadedTimestamp.value = Date.now();
                lastPage.value = page;
                lastLimit.value = limit;
                lastSearchId.value = res.headers['x-search-id'];
                totalRecords.value = parseInt(res.headers['x-filter-total-records'], 10);
                totalPages.value = parseInt(res.headers['x-filter-total-pages'], 10);
            });
            if (shouldSearchSimilarJobTitles) {
                await jobTitleSearchStore.loadSimilarJobTitles(searchFilters.value.title);
            }
        } finally {
            searchLoadingState.value = false;
        }
    }

    function syncCachedJob(job) {
        const existingJobIndex = searchResults.value.findIndex(j => j.id === job.id);
        if (existingJobIndex !== -1) {
            const existingJob = searchResults.value[existingJobIndex];
            for (const scope of existingJob.scopes) {
                delete loadedJobScopeMap.value[scope.id];
            }
            Object.assign(existingJob, job);
        } else {
            searchResults.value.push(job);
            loadedJobIds.value.add(job.id);
        }
        for (const scope of job.scopes) {
            loadedJobScopeMap.value[scope.id] = job.id;
        }
    }

    function prepareFilterParams(filter) {
        const queryParams = new URLSearchParams();
        const filterMappers = {
            location: v => ({ key: 'locationId', value: v?.id })
        };
        const validationMapper = {
            remunerationCycle: (v, f) => f.remunerationMin || f.remunerationMax
        };
        for (const [k, v] of Object.entries(filter)) {
            if (v === null || v === undefined || (Array.isArray(v) && v.length === 0) || (typeof v === 'string' && v.trim() === '')) continue;
            if (validationMapper[k] && !validationMapper[k](v, filter)) continue;
            const processed = filterMappers[k] ? filterMappers[k](v) : { key: k, value: v };
            if (Array.isArray(processed.value)) {
                processed.value.forEach(item => {
                    queryParams.append(`filter[${processed.key}][]`, item);
                });
            } else {
                queryParams.append(`filter[${processed.key}]`, processed.value);
            }
        }
        if (locationStore.browserPosition) {
            queryParams.append('filter[latitude]', locationStore.browserPosition.latitude);
            queryParams.append('filter[longitude]', locationStore.browserPosition.longitude);
        }
        if (searchSort.value) {
            const validatedSearchSort = sortOptions.find(o => o.value === searchSort.value)
                ? searchSort.value
                : SortOption.Relevance.value;
            queryParams.append('sort', validatedSearchSort);
        }
        return queryParams;
    }

    function generateBasicAuthToken() {
        return `Basic ${btoa([env.siteUsername, env.sitePassword].join(':'))}`;
    }

    function loadMoreSearchResults() {
        if (lastPage.value >= totalPages.value) {
            throw new Error('Total pages reached, try a new search query');
        }
        return loadSearchResults.bind(this)(lastPage.value + 1, lastLimit.value, false);
    }

    function resetSearchFilters() {
        searchFilters.value.title = null;
        searchFilters.value.classificationId = [];
        searchFilters.value.employmentType = [];
        searchFilters.value.workplaceLocation = [];
        searchFilters.value.remunerationCycle = null;
        searchFilters.value.remunerationMin = null;
        searchFilters.value.remunerationMax = null;
        searchFilters.value.exclusionTerm = [];
        searchFilters.value.maxAge = null;
        searchFilters.value.expandRemote = true;
        searchSort.value = sortOptions[0].value;
    }

    /**
     * Creates a job application by sending application data and files to the server.
     *
     * @param {number} jobScopeId - The ID of the job scope to which the application is being made.
     * @param {Object} applicationData - An object containing the data for the job application.
     * @param {number} applicationData.locationId - The ID of the location for the job application.
     * @param {string} applicationData.fullName - The full name of the applicant.
     * @param {string} applicationData.email - The email address of the applicant.
     * @param {string} applicationData.phone - The phone number of the applicant.
     * @param {Object} files - An object containing the file attachments for the application.
     * @param {File} files.resume - The resume file attachment for the application.
     * @param {File} files.coverLetter - The cover letter file attachment for the application.
     * @param {string} [captcha] - An optional captcha string for verification.
     * @return {Promise<Object>} A promise that resolves to the server's response data.
     */
    async function createJobApplication(jobScopeId, applicationData, files, captcha = undefined) {
        const formData = new FormData();
        formData.append('jsonData', JSON.stringify(applicationData));
        if (files.resume) {
            formData.append('resume', files.resume);
        }
        if (files.coverLetter) {
            formData.append('coverLetter', files.coverLetter);
        }
        return (await this.$api.postWithCaptcha(`/public/jobs/${jobScopeId}/application`, formData, { captcha })).data;
    }

    async function reportJob(jobScopeId, reportDetails, captcha = undefined) {
        return (await this.$api.postWithCaptcha(`/public/jobs/${jobScopeId}/report`, reportDetails, { captcha })).data;
    }

    piniaManager.onSessionTerminated(() => {
        // Leave all search results as-is so user can continue searching, except set all shortlisted flags to false
        // as the user has terminated their session.
        for (let i = 0; i < searchResults.value.length; i++) {
            const result = searchResults.value[i];
            for (let j = 0; j < result.scopes.length; j++) {
                result.scopes[j].shortlisted = false;
            }
        }
    });

    return {
        employmentTypeOptions,
        maxAgeOptions,
        remunerationCycleOptions,
        sortOptions,
        workLocationOptions,
        searchResults,
        searchFilters,
        appliedSearchFiltersCount,
        searchLoadedTimestamp,
        loadedSearchFilters,
        loadedSearchSort,
        totalRecords,
        searchSort,
        isSearchLoaded,
        isSearchLoading,
        hasMoreSearchResults,
        findJobByScopeId,
        syncCachedJob,
        loadSearchResults,
        loadMoreSearchResults,
        resetSearchFilters,
        createJobApplication,
        reportJob
    };
});
